import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import PageHero from '../components/Common/PageHero';
import { MigrationAccount, MigrationJobProgress, MigrationStepKey, MigrationUploadRow, migrationsAPI } from '../services/api';



const migrationAccounts: Array<{ value: MigrationAccount; label: string }> = [
  { value: 'datapool', label: 'Datapool' },
  { value: 'main', label: 'Main' },
  { value: 'impactplan', label: 'Impact Plan' },
];

const migrationAccountLabels: Record<MigrationAccount, string> = {
  datapool: 'Datapool',
  main: 'Main',
  impactplan: 'Impact Plan',
};

const migrationSteps: Array<{ key: MigrationStepKey; title: string; icon: string; requiredColumns: string[] }> = [
  {
    key: 'deactivate',
    title: '1. Deactivate service lines',
    icon: 'fas fa-power-off',
    requiredColumns: ['serviceLineNumber'],
  },
  {
    key: 'detach',
    title: '2. Remove kits from service lines',
    icon: 'fas fa-link-slash',
    requiredColumns: ['serviceLineNumber', 'kitNumber or deviceId'],
  },
  {
    key: 'removeFromSource',
    title: '3. Remove terminals from account',
    icon: 'fas fa-box-open',
    requiredColumns: ['kitNumber or deviceId'],
  },
  {
    key: 'addToTarget',
    title: '4. Add terminals to account',
    icon: 'fas fa-circle-plus',
    requiredColumns: ['kitNumber or deviceId'],
  },
  {
    key: 'resumeService',
    title: '5. Resume service lines',
    icon: 'fas fa-rotate-right',
    requiredColumns: ['serviceLineNumber'],
  },
  {
    key: 'attachAndResume',
    title: '6. Attach kit and resume service',
    icon: 'fas fa-plug-circle-bolt',
    requiredColumns: ['serviceLineNumber', 'kitNumber or deviceId'],
  },
  {
    key: 'optInPriorityData',
    title: '7. Opt in priority data',
    icon: 'fas fa-check-circle',
    requiredColumns: ['serviceLineNumber'],
  },
];

const normalizeHeader = (value: string) => value.replace(/[^a-z0-9]/gi, '').toLowerCase();

const getCell = (row: Record<string, any>, names: string[]) => {
  const entries = Object.entries(row);
  for (const name of names) {
    const normalizedName = normalizeHeader(name);
    const found = entries.find(([key]) => normalizeHeader(key) === normalizedName);
    if (found && found[1] !== undefined && found[1] !== null) return String(found[1]).trim();
  }
  return '';
};

const parseBoolean = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
  if (['false', 'no', 'n', '0'].includes(normalized)) return false;
  return false;
};

const rowHasRequiredFieldsForStep = (row: MigrationUploadRow, step: MigrationStepKey) => {
  if (step === 'deactivate') return Boolean(row.serviceLineNumber);
  if (step === 'detach') return Boolean(row.serviceLineNumber && row.deviceId);
  if (step === 'resumeService') return Boolean(row.serviceLineNumber);
  if (step === 'attachAndResume') return Boolean(row.serviceLineNumber && row.deviceId);
  if (step === 'optInPriorityData') return Boolean(row.serviceLineNumber);
  return Boolean(row.deviceId);
};

const requiredFieldsMessageForStep = (step: MigrationStepKey) => {
  if (step === 'deactivate') return 'This step needs serviceLineNumber for every row.';
  if (step === 'detach') return 'This step needs serviceLineNumber and deviceId or kitNumber for every row.';
  if (step === 'resumeService') return 'This step needs serviceLineNumber for every row. productReferenceId/productId is optional.';
  if (step === 'attachAndResume') return 'This step needs serviceLineNumber and deviceId or kitNumber for every row. productReferenceId/productId is optional.';
  return 'This step needs deviceId or kitNumber for every row.';
};

const formatRunDate = (value: string) => new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
}).format(new Date(value));

const jobStatusClasses: Record<MigrationJobProgress['status'], string> = {
  running: 'bg-blue-100 text-blue-800',
  done: 'bg-emerald-100 text-emerald-800',
  error: 'bg-rose-100 text-rose-800',
};

const Migrations: React.FC = () => {
  const navigate = useNavigate();
  const [selectedAccount, setSelectedAccount] = useState<MigrationAccount>('datapool');
  const [rows, setRows] = useState<MigrationUploadRow[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<MigrationJobProgress | null>(null);
  const [myJobs, setMyJobs] = useState<MigrationJobProgress[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [runningStep, setRunningStep] = useState<MigrationStepKey | null>(null);

  const totalBatches = useMemo(() => Math.ceil(rows.length / 100), [rows.length]);

  const loadMigrationJobs = async (selectActiveJob = false) => {
    try {
      setIsLoadingJobs(true);
      const response = await migrationsAPI.listJobs();
      setMyJobs(response.jobs);
      if (selectActiveJob) {
        const activeJob = response.jobs.find((candidate) => candidate.status === 'running');
        if (activeJob) {
          setJob(activeJob);
          setJobId(activeJob.id);
        }
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setMyJobs([]);
        return;
      }
      toast.error(error?.response?.data?.message || 'Failed to load your migration runs');
    } finally {
      setIsLoadingJobs(false);
    }
  };

  useEffect(() => {
    void loadMigrationJobs(true);
  }, []);

  useEffect(() => {
    if (!jobId) return;

    const interval = window.setInterval(async () => {
      try {
        const progress = await migrationsAPI.getJob(jobId);
        setJob(progress);
        if (progress.status !== 'running') {
          window.clearInterval(interval);
          void loadMigrationJobs();
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || 'Failed to fetch migration progress');
        window.clearInterval(interval);
      }
    }, 1500);

    return () => window.clearInterval(interval);
  }, [jobId]);

  const handleFileUpload = async (file?: File) => {
    if (!file) return;
    setIsParsing(true);
    setJob(null);
    setJobId(null);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonRows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });
      const parsedRows = jsonRows.map((row, index) => {
        const serviceLineNumber = getCell(row, ['serviceLineNumber', 'serviceLine', 'service line number', 'service line']);
        const deviceId = getCell(row, ['deviceId', 'device id', 'kitNumber', 'kit number', 'terminal']);
        const productReferenceId = getCell(row, ['productReferenceId', 'product reference id', 'productReferenceID', 'productId', 'product id']);
        const endNow = getCell(row, ['endNow', 'end now']);
        return {
          order: Number(getCell(row, ['order']) || index + 1),
          serviceLineNumber,
          deviceId,
          kitNumber: getCell(row, ['kitNumber', 'kit number']) || deviceId,
          productReferenceId,
          productId: productReferenceId,
          endNow: endNow ? parseBoolean(endNow) : false,
          reasonForCancellation: getCell(row, ['reasonForCancellation', 'reason', 'cancellation reason']) || 'Monthly migration',
        };
      }).filter((row) => row.serviceLineNumber || row.deviceId || row.productReferenceId);

      if (parsedRows.length === 0) {
        toast.error('No valid rows found. Include serviceLineNumber, deviceId/kitNumber, or productReferenceId/productId.');
        setRows([]);
        return;
      }

      setRows(parsedRows.sort((left, right) => left.order - right.order));
      toast.success(`Loaded ${parsedRows.length} migration rows`);
    } catch (error) {
      console.error(error);
      toast.error('Could not read the uploaded Excel/CSV file');
    } finally {
      setIsParsing(false);
    }
  };

  const startMigrationStep = async (step: MigrationStepKey) => {
    if (rows.length === 0) {
      toast.error('Upload a migration file first');
      return;
    }
    if (rows.some((row) => !rowHasRequiredFieldsForStep(row, step))) {
      toast.error(requiredFieldsMessageForStep(step));
      return;
    }

    try {
      setRunningStep(step);
      const response = await migrationsAPI.start({ step, account: selectedAccount, rows });
      setJobId(response.jobId);
      const progress = await migrationsAPI.getJob(response.jobId);
      setJob(progress);
      void loadMigrationJobs();
      toast.success('Migration step started');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to start migration');
    } finally {
      setRunningStep(null);
    }
  };

  const downloadResults = () => {
    if (!job?.rows?.length) {
      toast.error('No migration results to download yet');
      return;
    }

    const exportRows = job.rows.map((row) => ({
      order: row.order,
      serviceLineNumber: row.serviceLineNumber || '',
      kitNumber: row.deviceId || '',
      productReferenceId: row.productReferenceId || '',
      result: row.status,
      message: row.message || '',
      step: job.stepLabel,
      account: migrationAccountLabels[job.account],
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Migration Results');
    XLSX.writeFile(workbook, `migration-results-${job.id}.xlsx`);
  };

  const percentComplete = job?.percentComplete ?? 0;

  return (
    <div className="space-y-8">
      <PageHero
        badge={{ label: 'Monthly migration runner', tone: 'cyan' }}
        title="Migrations"
        description="Upload migration rows once, then run each Starlink migration step separately at your own pace."
        stats={[
          { icon: 'fas fa-layer-group', label: rows.length ? `${rows.length} rows loaded` : 'No file uploaded' },
          { icon: 'fas fa-boxes-stacked', label: rows.length ? `${totalBatches} batch${totalBatches === 1 ? '' : 'es'} of 100` : 'Batch size: 100' },
        ]}
        actions={
          <button
            onClick={() => navigate('/migrations/history')}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white px-5 py-3 text-sm font-black text-slate-900 shadow-lg transition hover:bg-cyan-50"
          >
            <i className="fas fa-clock-rotate-left"></i>
            History
          </button>
        }
      />

      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-black text-gray-900">My Migration Runs</h2>
            <p className="mt-1 text-sm text-gray-600">Your active and recent runs stay here, even if you log out and come back.</p>
          </div>
          <button
            onClick={() => loadMigrationJobs()}
            disabled={isLoadingJobs}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            <i className={`fas ${isLoadingJobs ? 'fa-spinner animate-spin' : 'fa-rotate'}`}></i>
            Refresh
          </button>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {myJobs.length ? myJobs.slice(0, 6).map((migrationJob) => (
            <button
              key={migrationJob.id}
              onClick={() => {
                setJob(migrationJob);
                setJobId(migrationJob.id);
              }}
              className={`rounded-2xl border p-4 text-left transition hover:border-blue-200 hover:bg-blue-50/40 ${jobId === migrationJob.id ? 'border-blue-300 bg-blue-50/60' : 'border-gray-100 bg-gray-50'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-black text-gray-900">{migrationJob.stepLabel}</div>
                  <div className="mt-1 text-xs font-semibold text-gray-500">{migrationAccountLabels[migrationJob.account]} · {formatRunDate(migrationJob.createdAt)}</div>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-black uppercase ${jobStatusClasses[migrationJob.status]}`}>
                  {migrationJob.status}
                </span>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-white">
                <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500" style={{ width: `${migrationJob.percentComplete}%` }} />
              </div>
              <div className="mt-3 flex items-center justify-between text-xs font-bold text-gray-600">
                <span>{migrationJob.percentComplete}% complete</span>
                <span>{migrationJob.successfulRows} success · {migrationJob.failedRows} failed</span>
              </div>
            </button>
          )) : (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-5 text-sm font-semibold text-gray-500">
              No migration runs for your user yet.
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="text-xl font-black text-gray-900">1. Upload migration file</h2>
              <p className="mt-1 text-sm text-gray-600">For resume, upload serviceLineNumber. productReferenceId/productId is optional and will be fetched when blank.</p>
            </div>

            <label className="flex cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-6 py-10 text-center transition hover:bg-blue-50">
              <i className="fas fa-file-arrow-up mb-3 text-3xl text-[#4169E1]"></i>
              <span className="font-bold text-gray-900">{isParsing ? 'Reading file…' : 'Choose Excel or CSV file'}</span>
              <span className="mt-1 text-sm text-gray-500">Supports .xlsx, .xls, and .csv</span>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(event) => handleFileUpload(event.target.files?.[0])}
              />
            </label>

            <div className="grid grid-cols-1 gap-4">
              <label className="space-y-2">
                <span className="text-sm font-bold text-gray-700">Account to run this stage on</span>
                <select value={selectedAccount} onChange={(event) => setSelectedAccount(event.target.value as MigrationAccount)} className="w-full rounded-2xl border border-gray-200 px-4 py-3 font-semibold text-gray-800">
                  {migrationAccounts.map((account) => (
                    <option key={account.value} value={account.value}>{account.label}</option>
                  ))}
                </select>
              </label>
            </div>

          </div>
        </div>

        <div className="xl:col-span-2 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {migrationSteps.map((step) => {
            const isRunning = runningStep === step.key || (job?.status === 'running' && job.step === step.key);
            return (
              <div key={step.key} className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-[#4169E1]">
                    <i className={`${step.icon} text-xl`}></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-black text-gray-900">{step.title}</h2>
                    <p className="mt-2 text-xs font-semibold text-gray-500">
                      {step.key === 'resumeService' || step.key === 'attachAndResume'
                        ? `Tries all accounts, starting with: ${migrationAccountLabels[selectedAccount]}`
                        : `Selected account: ${migrationAccountLabels[selectedAccount]}`}
                    </p>
                    <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-3">
                      <div className="flex flex-wrap gap-2">
                        {step.requiredColumns.map((column) => (
                          <span key={`${step.key}-required-${column}`} className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-800">
                            Required: {column}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => startMigrationStep(step.key)}
                  disabled={Boolean(runningStep) || rows.length === 0 || job?.status === 'running'}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-bold text-white shadow-lg transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  <i className={`fas ${isRunning ? 'fa-spinner animate-spin' : 'fa-play'}`}></i>
                  Run {step.title}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-xl font-black text-gray-900">Progress</h2>
            <button
              onClick={downloadResults}
              disabled={!job?.rows?.length}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              <i className="fas fa-file-excel"></i>
              Download Excel Results
            </button>
          </div>
          <div className="mt-5">
            <div className="mb-2 flex items-center justify-between text-sm font-bold text-gray-700">
              <span>{percentComplete}% complete</span>
              <span>{job ? `${job.stepLabel} · Batch ${job.currentBatch}/${job.totalBatches}` : 'Waiting'}</span>
            </div>
            <div className="h-4 overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all" style={{ width: `${percentComplete}%` }} />
            </div>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-2xl bg-gray-50 p-4">
              <div className="text-2xl font-black text-gray-900">{job?.processedRows ?? 0}</div>
              <div className="text-xs font-semibold text-gray-500">Processed</div>
            </div>
            <div className="rounded-2xl bg-emerald-50 p-4">
              <div className="text-2xl font-black text-emerald-700">{job?.successfulRows ?? 0}</div>
              <div className="text-xs font-semibold text-emerald-700">Success</div>
            </div>
            <div className="rounded-2xl bg-rose-50 p-4">
              <div className="text-2xl font-black text-rose-700">{job?.failedRows ?? 0}</div>
              <div className="text-xs font-semibold text-rose-700">Failed</div>
            </div>
          </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-xl font-black text-gray-900">Uploaded Rows</h2>
            <button
              onClick={downloadResults}
              disabled={!job?.rows?.length}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 px-4 py-2 text-sm font-bold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
            >
              <i className="fas fa-download"></i>
              Export
            </button>
          </div>
          <div className="mt-4 max-h-96 overflow-auto rounded-2xl border border-gray-100">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-bold text-gray-600">Order</th>
                  <th className="px-4 py-3 text-left font-bold text-gray-600">Service Line</th>
                  <th className="px-4 py-3 text-left font-bold text-gray-600">Device / Kit</th>
                  <th className="px-4 py-3 text-left font-bold text-gray-600">Product ID</th>
                  <th className="px-4 py-3 text-left font-bold text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {(job?.rows || rows).slice(0, 300).map((row: any) => (
                  <tr key={`${row.order}-${row.serviceLineNumber}-${row.deviceId}`}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{row.order}</td>
                    <td className="px-4 py-3 text-gray-700">{row.serviceLineNumber || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-700">{row.deviceId}</td>
                    <td className="px-4 py-3 text-gray-700">{row.productReferenceId || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-700">{row.status || 'ready'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-slate-950 p-6 shadow-sm">
          <h2 className="text-xl font-black text-white">Logs</h2>
          <div className="mt-4 max-h-96 overflow-auto rounded-2xl bg-black/30 p-4 font-mono text-xs text-slate-200">
            {job?.logs?.length ? job.logs.map((line) => <div key={line}>{line}</div>) : <div className="text-slate-500">Logs will appear here once the migration starts.</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Migrations;
